import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import {
    customerInfoProviderContextToken,
    urlsBuilderContextToken,
} from "@/context_tokens";
import { customerInfoProviderToken, growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { ShareWishlistsQuery } from "@apps/wishlist/queries/ShareWishlistsQuery";
import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
import { WishlistsQuery } from "@apps/wishlist/queries/WishlistsQuery";
import { ShareWishlistsService } from "@apps/wishlist/services/ShareWishlistsService";
import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
import WishlistsBackInStockItemsService from "@apps/wishlist/services/WishlistsBackInStockItemsService";
import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import WindowPopupManager from "@lib/WindowPopupManager/WindowPopupManager";
import { AuthenticationInfoProvider } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProvider";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { CartModule } from "@modules/cart/CartModule";
import { CartQuery } from "@modules/cart/queries/CartQuery";
import { CartService } from "@modules/cart/services/CartService";
import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import GrowaveStorefrontValuesModule from "@modules/growave_storefront_values/GrowaveStorefrontValuesModule";
import GrowaveStorefrontValuesService from "@modules/growave_storefront_values/services/GrowaveStorefrontValuesService";
import { I18nModule } from "@modules/i18n/I18nModule";
import { ProductsModule } from "@modules/products/ProductsModule";
import { CombinedProductsAndProductsWithQuantityRulesQuery } from "@modules/products/queries/CombinedProductsAndProductsWithQuantityRulesQuery";
import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import {
    productsQueryToken,
    productsServiceToken,
    productsWithQuantityRulesQueryToken,
} from "@modules/products/tokens";
import { container, injectable } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import WishlistPageWidgetComponent from "./WishlistPageWidget.svelte";
import { WishlistPageWidgetConfigInterface } from "./WishlistPageWidgetConfig";

@injectable()
class WishlistPageWidget implements WidgetInterface {
    private wishlistsService: WishlistsService;
    private wishlistsQuery: WishlistsQuery;
    private wishlistsBackInStockItemsService: WishlistsBackInStockItemsService;
    private wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    private wishlistProductStatusesService: WishlistProductStatusesService;
    private wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    private wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    private productsService: ProductsServiceInterface;
    private productsQuery: ProductsQuery;
    private combinedProductsAndProductsWithQuantityRulesQuery: CombinedProductsAndProductsWithQuantityRulesQuery;
    private productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    private cartService: CartService;
    private cartQuery: CartQuery;
    private shareWishlistsService: ShareWishlistsService;
    private shareWishlistsQuery: ShareWishlistsQuery;
    private growaveStorefrontValuesService: GrowaveStorefrontValuesService;
    private authenticationInfoProvider: AuthenticationInfoProvider;
    private cartAvailabilityProvider: CartAvailabilityProvider;
    private customerInfoProvider: CustomerInfoProviderInterface;

    constructor(
        private readonly config: WishlistPageWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly modalManager: ModalManager,
        private readonly dynamicImportManager: DynamicImportManager
    ) {
        this.logger.debug("WishlistPageWidget is initialized", config);

        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "wishlist.WishlistPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "wishlist.WishlistPageWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "wishlist.critical.WishlistPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "wishlist.critical.WishlistPageWidget"
            )
        );

        this.wishlistsService = container.resolve(WishlistsService);
        this.wishlistsQuery = container.resolve(WishlistsQuery);
        this.wishlistsBackInStockItemsService = container.resolve(
            WishlistsBackInStockItemsService
        );
        this.wishlistsBackInStockItemsQuery = container.resolve(
            WishlistsBackInStockItemsQuery
        );
        this.wishlistProductStatusesService = container.resolve(
            WishlistProductStatusesService
        );
        this.wishlistProductStatusesService.subscribe();
        this.wishlistProductStatusesQuery = container.resolve(
            WishlistProductStatusesQuery
        );
        this.wishlistsStorefrontValuesService = container.resolve(
            WishlistsStorefrontValuesService
        );
        this.productsService = container.resolve(productsServiceToken);
        this.productsQuery = container.resolve(productsQueryToken);
        this.combinedProductsAndProductsWithQuantityRulesQuery =
            container.resolve(
                ProductsModule
            ).exports.combinedProductsAndProductsWithQuantityRulesQuery;

        this.productsWithQuantityRulesQuery = container.resolve(
            productsWithQuantityRulesQueryToken
        );
        this.cartService = container.resolve(CartModule).exports.cartService;
        this.cartQuery = container.resolve(CartModule).exports.cartQuery;
        this.shareWishlistsService = container.resolve(ShareWishlistsService);
        this.shareWishlistsQuery = container.resolve(ShareWishlistsQuery);
        this.growaveStorefrontValuesService = container.resolve(
            GrowaveStorefrontValuesModule
        ).exports.growaveStorefrontValuesService;
        this.authenticationInfoProvider =
            container.resolve(
                AuthenticationModule
            ).exports.authenticationInfoProvider;
        this.cartAvailabilityProvider =
            container.resolve(CommonModule).exports.cartAvailabilityProvider;
        this.customerInfoProvider = container.resolve(
            customerInfoProviderToken
        );
    }

    async init(): Promise<this> {
        this.productsService.subscribe();
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );
        const gwI18n = container.resolve(I18nModule).exports.i18n;
        const locationController =
            container.resolve(CommonModule).exports.locationController;
        const modulesStatusesProvider =
            container.resolve(BaseModule).exports.modulesStatusesProvider;

        const element = this.config.target;
        element.style.display = "block";
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: WISHLIST_WIDGET_NAMES.WISHLIST_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );

        contextMap.set(
            customerInfoProviderContextToken,
            this.customerInfoProvider
        );

        new WishlistPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                productsQuery: this.productsQuery,
                combinedProductsAndProductsWithQuantityRulesQuery:
                    this.combinedProductsAndProductsWithQuantityRulesQuery,
                productsWithQuantityRulesQuery:
                    this.productsWithQuantityRulesQuery,
                wishlistsQuery: this.wishlistsQuery,
                wishlistsBackInStockItemsService:
                    this.wishlistsBackInStockItemsService,
                wishlistsBackInStockItemsQuery:
                    this.wishlistsBackInStockItemsQuery,
                wishlistProductStatusesQuery: this.wishlistProductStatusesQuery,
                wishlistsStorefrontValuesService:
                    this.wishlistsStorefrontValuesService,
                wishlistsService: this.wishlistsService,
                cartService: this.cartService,
                cartQuery: this.cartQuery,
                modalManager: this.modalManager,
                growaveStorefrontValuesService:
                    this.growaveStorefrontValuesService,
                shareWishlistsService: this.shareWishlistsService,
                shareWishlistsQuery: this.shareWishlistsQuery,
                authenticationInfoProvider: this.authenticationInfoProvider,
                gwI18n: gwI18n,
                modulesStatusesProvider: modulesStatusesProvider,
                locationController: locationController,
                growaveConfiguration: growaveConfiguration,
                dynamicImportManager: this.dynamicImportManager,
                cartAvailabilityProvider: this.cartAvailabilityProvider,
                windowPopupManager: new WindowPopupManager(),
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
                wishlistItemOptionsEditable:
                    this.config.wishlistItemOptionsEditable,
            },
            context: contextMap,
        });

        return this;
    }
}

export default WishlistPageWidget;
